import { apiRequest } from "./services";
import { isServiceUnavailableResult } from "./apiAvailability";
import { accountReviewCode } from "../authentication/Login/accountReview";

export const SESSION_TOKEN_KEY = "jwt_token";

// Waits between users/token attempts while the API is unavailable (4 tries).
export const SESSION_TOKEN_RETRY_DELAYS_MS = [1000, 2000, 4000];

// Jitter so clients reloading together don't retry in lockstep.
const waitWithJitter = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms * (0.75 + Math.random() / 2)));

/**
 * Exchanges the Firebase uid for an API JWT via users/token. Retries with
 * backoff while the API is unavailable (5xx / network failure) instead of
 * handing back an empty token, which would 401 the next request and sign the
 * user out.
 *
 * Sends the Firebase id_token with the uid so the API can check the token
 * belongs to that uid.
 *
 * Resolves one of:
 *   { status: "ok", accessToken }
 *   { status: "unavailable", result }  — API down after all retries
 *   { status: "review", code, result } — the account waits for approval or was rejected
 *   { status: "rejected", result }     — the API answered and refused
 */
export const requestSessionToken = async (
  userId,
  {
    request = apiRequest,
    wait = waitWithJitter,
    retryDelaysMs = SESSION_TOKEN_RETRY_DELAYS_MS,
    getIdToken = async () => null,
    storage = window.localStorage,
  } = {}
) => {
  dropForeignSessionToken(userId, { storage });
  let idToken = null;
  try {
    idToken = await getIdToken();
  } catch (_) {
    // Sent without one; the API logs it and still answers while the check is not enforced.
  }
  const payload = idToken ? { user_id: userId, id_token: idToken } : { user_id: userId };
  for (let attempt = 0; ; attempt += 1) {
    const result = await request("users/token", "POST", payload, true);
    if (result?.access_token) {
      return { status: "ok", accessToken: result.access_token };
    }
    const reviewCode = accountReviewCode(result);
    if (reviewCode) {
      return { status: "review", code: reviewCode, result };
    }
    if (!isServiceUnavailableResult(result)) {
      return { status: "rejected", result };
    }
    if (attempt >= retryDelaysMs.length) {
      return { status: "unavailable", result };
    }
    await wait(retryDelaysMs[attempt]);
  }
};

const decodeJwtPayload = (jwt) => {
  const part = String(jwt).split(".")[1];
  if (!part) return null;
  return JSON.parse(window.atob(part.replace(/-/g, "+").replace(/_/g, "/")));
};

/**
 * Removes a stored API JWT that was not issued to this Firebase user, such as
 * the previous account's JWT after switching accounts in the same browser.
 * apiRequest sends whatever JWT is stored, and the API's actor guard refuses
 * users/token when the JWT's uid is not the user_id asked for, so the next
 * account's first sign-in was signed out. The user's own JWT is kept, expired
 * or not.
 */
export const dropForeignSessionToken = (
  userId,
  { storage = window.localStorage } = {}
) => {
  try {
    const raw = storage.getItem(SESSION_TOKEN_KEY);
    if (!raw) return false;
    const item = JSON.parse(raw);
    if (item?.value && decodeJwtPayload(item.value)?.sub === userId) return false;
  } catch (_) {
    // Unreadable: not this user's token either.
  }
  storage.removeItem(SESSION_TOKEN_KEY);
  return true;
};

/**
 * True when localStorage already holds an unexpired API JWT issued to this
 * Firebase user (`sub` claim), so the app can keep working with it while
 * users/token is unavailable. The owner check keeps a previous user's token
 * from being reused after an account switch on the same browser.
 */
export const hasUsableSessionToken = (
  userId,
  { storage = window.localStorage, now = Date.now() } = {}
) => {
  try {
    const item = JSON.parse(storage.getItem(SESSION_TOKEN_KEY));
    if (!item?.value || !(now < item.expiry)) return false;
    return decodeJwtPayload(item.value)?.sub === userId;
  } catch (_) {
    return false;
  }
};
